/** Shared hook for the caller's own enterprise branding (name + logo).
 *
 * Backed by `GET /api/enterprises/mine`, which every tenant role can
 * read regardless of scope — so a state_head or district_head, who
 * hold a STATE/DISTRICT scope rather than an ENTERPRISE one, still get
 * their employer's logo for the header. agrilens_super has no home
 * enterprise, so `enterprise` comes back null and we render nothing
 * extra.
 *
 * The result is cached for the session (staleTime Infinity) — the
 * logo changes at most once in a blue moon, and any page that updates
 * it invalidates ["my-enterprise"] explicitly. */

import { useQuery } from "@tanstack/react-query";

import { Enterprise, myEnterprise } from "./api";
import { currentClaims } from "./auth";

export const MY_ENTERPRISE_KEY = ["my-enterprise"];

export interface Branding {
  enterprise: Enterprise | null;
  isLoading: boolean;
}

export function useEnterpriseBranding(): Branding {
  const claims = currentClaims();
  const q = useQuery({
    queryKey: MY_ENTERPRISE_KEY,
    queryFn: myEnterprise,
    // Only tenant users have a home enterprise; skip the call when
    // signed out. Super still calls it (returns null) so the hook is
    // uniform, but there's no point firing it with no token.
    enabled: !!claims,
    staleTime: Infinity,
    retry: false,
  });
  return {
    enterprise: q.data?.enterprise ?? null,
    isLoading: q.isLoading,
  };
}
